import { DEFAULT_ENVIRONMENT } from '../constants.js';
import { getClient } from '../currentScopes.js';
import { SEMANTIC_ATTRIBUTE_SENTRY_SAMPLE_RATE, SEMANTIC_ATTRIBUTE_SENTRY_SOURCE } from '../semanticAttributes.js';
import { baggageHeaderToDynamicSamplingContext, dynamicSamplingContextToSentryBaggageHeader } from '../utils-hoist/baggage.js';
import { dropUndefinedKeys, addNonEnumerableProperty } from '../utils-hoist/object.js';
import { hasSpansEnabled } from '../utils/hasSpansEnabled.js';
import { getRootSpan, spanToJSON, spanIsSampled } from '../utils/spanUtils.js';
import { getCapturedScopesOnSpan } from './utils.js';
/**
 * If you change this value, also update the terser plugin config to
 * avoid minification of the object property!
 */

var FROZEN_DSC_FIELD = '_frozenDsc';
/**
 * Freeze the given DSC on the given span.
 */

function freezeDscOnSpan(span, dsc) {
  var spanWithMaybeDsc = span;
  addNonEnumerableProperty(spanWithMaybeDsc, FROZEN_DSC_FIELD, dsc);
}
/**
 * Creates a dynamic sampling context from a client.
 *
 * Dispatches the `createDsc` lifecycle hook as a side effect.
 */


function getDynamicSamplingContextFromClient(trace_id, client) {
  var options = client.getOptions();
  var {
    publicKey: public_key
  } = client.getDsn() || {};
  var dsc = dropUndefinedKeys({
    environment: options.environment || DEFAULT_ENVIRONMENT,
    release: options.release,
    public_key,
    trace_id
  });
  client.emit('createDsc', dsc);
  return dsc;
}
/**
 * Get the dynamic sampling context for the currently active scopes.
 */


function getDynamicSamplingContextFromScope(client, scope) {
  var propagationContext = scope.getPropagationContext();
  return propagationContext.dsc || getDynamicSamplingContextFromClient(propagationContext.traceId, client);
}
/**
 * Creates a dynamic sampling context from a span (and client and scope)
 *
 * @param span the span from which a few values like the root span name and sample rate are extracted.
 *
 * @returns a dynamic sampling context
 */


function getDynamicSamplingContextFromSpan(span) {
  var _traceState$get;

  var client = getClient();

  if (!client) {
    return {};
  }

  var rootSpan = getRootSpan(span);
  var rootSpanJson = spanToJSON(rootSpan);
  var rootSpanAttributes = rootSpanJson.data;
  var traceState = rootSpan.spanContext().traceState; // The span sample rate that was locally applied to the root span should also always be applied to the DSC, even if the DSC is frozen.
  // This is so that the downstream traces/services can use parentSampleRate in their `tracesSampler` to make consistent sampling decisions across the entire trace.

  var rootSpanSampleRate = (_traceState$get = traceState === null || traceState === void 0 ? void 0 : traceState.get('sentry.sample_rate')) !== null && _traceState$get !== void 0 ? _traceState$get : rootSpanAttributes[SEMANTIC_ATTRIBUTE_SENTRY_SAMPLE_RATE];

  function applyLocalSampleRateToDsc(dsc) {
    if (typeof rootSpanSampleRate === 'number' || typeof rootSpanSampleRate === 'string') {
      dsc.sample_rate = "".concat(rootSpanSampleRate);
    }

    return dsc;
  } // For core implementation, we freeze the DSC onto the span as a non-enumerable property


  var frozenDsc = rootSpan[FROZEN_DSC_FIELD];

  if (frozenDsc) {
    return applyLocalSampleRateToDsc(frozenDsc);
  } // For OpenTelemetry, we freeze the DSC on the trace state


  var traceStateDsc = traceState === null || traceState === void 0 ? void 0 : traceState.get('sentry.dsc'); // If the span has a DSC, we want it to take precedence

  var dscOnTraceState = traceStateDsc && baggageHeaderToDynamicSamplingContext(traceStateDsc);

  if (dscOnTraceState) {
    return applyLocalSampleRateToDsc(dscOnTraceState);
  } // Else, we generate it from the span


  var dsc = getDynamicSamplingContextFromClient(span.spanContext().traceId, client); // We don't want to have a transaction name in the DSC if the source is "url" because URLs might contain PII

  var source = rootSpanAttributes[SEMANTIC_ATTRIBUTE_SENTRY_SOURCE]; // after JSON conversion, txn.name becomes jsonSpan.description

  var name = rootSpanJson.description;

  if (source !== 'url' && name) {
    dsc.transaction = name;
  } // How can we even land here with hasSpansEnabled() returning false?
  // Otel creates a Non-recording span in Tracing Without Performance mode when handling incoming requests
  // So we end up with an active span that is not sampled (neither positively nor negatively)


  if (hasSpansEnabled()) {
    var _traceState$get2, _getCapturedScopesOnS;

    dsc.sampled = String(spanIsSampled(rootSpan));
    dsc.sample_rand = // In OTEL we store the sample rand on the trace state because we cannot access scopes for NonRecordingSpans
    // The Sentry OTEL SpanSampler takes care of writing the sample rand on the root span
    (_traceState$get2 = traceState === null || traceState === void 0 ? void 0 : traceState.get('sentry.sample_rand')) !== null && _traceState$get2 !== void 0 ? _traceState$get2 : // On all other platforms we can actually get the scopes from a root span (we use this as a fallback)
    (_getCapturedScopesOnS = getCapturedScopesOnSpan(rootSpan).scope) === null || _getCapturedScopesOnS === void 0 ? void 0 : _getCapturedScopesOnS.getPropagationContext().sampleRand.toString();
  }

  applyLocalSampleRateToDsc(dsc);
  client.emit('createDsc', dsc, rootSpan);
  return dsc;
}
/**
 * Convert a Span to a baggage header.
 */


function spanToBaggageHeader(span) {
  var dsc = getDynamicSamplingContextFromSpan(span);
  return dynamicSamplingContextToSentryBaggageHeader(dsc);
}

export { freezeDscOnSpan, getDynamicSamplingContextFromClient, getDynamicSamplingContextFromScope, getDynamicSamplingContextFromSpan, spanToBaggageHeader };